export type RespiteViewState = {
  fromMonth: string;
  toMonth: string;
  clinic: string;
  navigator: string;
  provider: string;
};

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** YYYY-MM shifted by whole months. */
export function shiftMonth(month: string, delta: number): string {
  const [year, monthIndex] = month.split("-").map(Number) as [number, number];
  const shifted = new Date(Date.UTC(year, monthIndex - 1 + delta, 1));
  return shifted.toISOString().slice(0, 7);
}

/** Initial-PAF window: the first of `fromMonth` through the end of `toMonth`, capped at today. */
export function respiteWindow(state: RespiteViewState, today: string) {
  const end = `${shiftMonth(state.toMonth, 1)}-01`;
  const lastDay = new Date(Date.parse(`${end}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  return { dateStart: `${state.fromMonth}-01`, dateEnd: lastDay < today ? lastDay : today };
}

export function defaultRespiteState(currentMonth: string): RespiteViewState {
  return {
    fromMonth: shiftMonth(currentMonth, -11),
    toMonth: currentMonth,
    clinic: "",
    navigator: "",
    provider: "",
  };
}

export function readRespiteState(search: string, currentMonth: string): RespiteViewState {
  const params = new URLSearchParams(search);
  const defaults = defaultRespiteState(currentMonth);
  const month = (key: string, fallback: string) => {
    const value = params.get(key) ?? "";
    return MONTH_PATTERN.test(value) && value <= currentMonth ? value : fallback;
  };
  let fromMonth = month("from", defaults.fromMonth);
  let toMonth = month("to", defaults.toMonth);
  if (fromMonth > toMonth) [fromMonth, toMonth] = [toMonth, fromMonth];
  return {
    fromMonth,
    toMonth,
    clinic: params.get("clinic")?.trim() ?? "",
    navigator: params.get("cn")?.trim() ?? "",
    provider: params.get("provider")?.trim() ?? "",
  };
}

/** Writes only the values that differ from the defaults, preserving unrelated params. */
export function writeRespiteState(
  search: string,
  state: RespiteViewState,
  currentMonth: string,
): string {
  const params = new URLSearchParams(search);
  const defaults = defaultRespiteState(currentMonth);
  const entries: Array<[string, string, string]> = [
    ["from", state.fromMonth, defaults.fromMonth],
    ["to", state.toMonth, defaults.toMonth],
    ["clinic", state.clinic, ""],
    ["cn", state.navigator, ""],
    ["provider", state.provider, ""],
  ];
  for (const [key, value, fallback] of entries) {
    params.delete(key);
    if (value && value !== fallback) params.set(key, value);
  }
  return params.toString() ? `?${params}` : "";
}
